/**
 * Copy Dialog - Folder selection for copying files
 *
 * Displays a folder tree for selecting the destination folder
 * when copying files/folders. Includes optional rename field.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TreeNode, FileNode } from '@/types/files';
import { getFileTree, copyFile, bulkCopyFiles } from '@/services/filesApi';

interface CopyDialogProps {
  isOpen: boolean;
  files: FileNode[];
  onClose: () => void;
  onCopyComplete: () => void;
}

const CopyDialog: React.FC<CopyDialogProps> = ({
  isOpen,
  files,
  onClose,
  onCopyComplete,
}) => {
  const { t } = useTranslation('files');
  const [tree, setTree] = useState<TreeNode[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [newName, setNewName] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [copying, setCopying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Single file mode allows renaming
  const isSingleFile = files.length === 1;

  useEffect(() => {
    if (isOpen) {
      loadTree();
      // Default new name for single file
      if (isSingleFile) {
        const file = files[0];
        const ext = file.name.includes('.') ? '.' + file.name.split('.').pop() : '';
        const baseName = file.name.replace(ext, '');
        setNewName(`${baseName} (Copy)${ext}`);
      }
    }
  }, [isOpen, files]);

  const loadTree = async () => {
    setLoading(true);
    setError(null);
    try {
      const treeData = await getFileTree(null, 10, false);
      setTree(treeData);
      // Expand all folders by default
      const allFolderIds = new Set<string>();
      const collectIds = (nodes: TreeNode[]) => {
        for (const node of nodes) {
          if (node.node.is_folder) {
            allFolderIds.add(node.node.id);
            if (node.children) {
              collectIds(node.children);
            }
          }
        }
      };
      collectIds(treeData);
      setExpandedFolders(allFolderIds);
    } catch (err) {
      setError(t('copy_dialog.error_load_folders'));
      console.error('Failed to load tree:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleFolder = (folderId: string) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  };

  const handleCopy = async () => {
    setCopying(true);
    setError(null);
    try {
      if (isSingleFile) {
        await copyFile(files[0].id, {
          dest_parent_id: selectedFolderId,
          new_name: newName.trim() || undefined,
        });
      } else {
        await bulkCopyFiles({
          file_ids: files.map(f => f.id),
          dest_parent_id: selectedFolderId,
        });
      }
      onCopyComplete();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('copy_dialog.error_copy'));
      console.error('Copy failed:', err);
    } finally {
      setCopying(false);
    }
  };

  const renderTreeNode = (node: TreeNode, level: number = 0): React.ReactNode => {
    const { node: fileNode, children } = node;

    // Only show folders
    if (!fileNode.is_folder) return null;

    const isExpanded = expandedFolders.has(fileNode.id);
    const isSelected = selectedFolderId === fileNode.id;
    const hasChildren = children && children.some(c => c.node.is_folder);

    return (
      <div key={fileNode.id}>
        <div
          className={`
            flex items-center gap-2 py-1.5 px-2 rounded cursor-pointer
            ${isSelected ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}
          `}
          style={{ paddingLeft: `${level * 16 + 8}px` }}
          onClick={() => setSelectedFolderId(fileNode.id)}
        >
          {/* Expand/collapse toggle */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleFolder(fileNode.id);
            }}
            className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600"
          >
            {hasChildren ? (
              <svg
                className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            ) : (
              <span className="w-3 h-3" />
            )}
          </button>

          {/* Folder icon */}
          <svg className="w-5 h-5 text-yellow-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
          </svg>

          {/* Folder name */}
          <span className="truncate text-sm">{fileNode.name}</span>
        </div>

        {/* Children */}
        {isExpanded && children && (
          <div>
            {children.map(child => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {files.length === 1 ? t('copy_dialog.title_single', { name: files[0].name }) : t('copy_dialog.title_multi', { count: files.length })}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-3">
          {/* New name input (single file only) */}
          {isSingleFile && (
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('copy_dialog.new_name_label')}
              </label>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={t('copy_dialog.new_name_placeholder')}
              />
            </div>
          )}

          <p className="text-sm text-gray-600 mb-3">{t('copy_dialog.select_destination')}</p>

          {/* Root folder option */}
          <div
            className={`
              flex items-center gap-2 py-1.5 px-2 rounded cursor-pointer mb-2
              ${selectedFolderId === null ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}
            `}
            onClick={() => setSelectedFolderId(null)}
          >
            <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            <span className="text-sm font-medium">{t('copy_dialog.root_disk')}</span>
          </div>

          {/* Folder tree */}
          <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
            {loading ? (
              <div className="p-4 text-center text-gray-500">
                {t('copy_dialog.loading_folders')}
              </div>
            ) : tree.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                {t('copy_dialog.no_folders')}
              </div>
            ) : (
              <div className="py-2">
                {tree.map(node => renderTreeNode(node, 0))}
              </div>
            )}
          </div>

          {error && (
            <p className="mt-2 text-sm text-red-600">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {t('common:buttons.cancel')}
          </button>
          <button
            onClick={handleCopy}
            disabled={copying}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {copying ? t('copy_dialog.copying') : t('copy_dialog.copy_here')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CopyDialog;
